import { useQuery } from '@tanstack/react-query';
import { getMyPermissions, getPermissionCategories } from '@app/api/routes/mce/permissions';
import useAuth from '@app/hooks/useAuth';

const QUERY_KEY = ['mce', 'permission-categories'] as const;

/**
 * Prefixe partage de la queryKey des permissions du user courant.
 * Reste utilise tel quel par les invalidations (qui matchent par prefixe)
 * dans useAdminUsers et useFunctions — chaque queryKey scoped par userId
 * commence par ce prefixe.
 */
export const MY_PERMISSIONS_KEY = ['mce', 'my-permissions'] as const;

/**
 * Construit la queryKey scoped par userId. CW-22023 : sans le userId dans
 * la clé, le cache React Query (staleTime 5 min) renvoie les permissions
 * de l'utilisateur precedent quand on alterne 2 sessions sur la meme onglet
 * sans cold reload (le menu MCE/ADMIN fuit d'un user a l'autre).
 */
export const getMyPermissionsKey = (userId: number | undefined) =>
  [...MY_PERMISSIONS_KEY, userId] as const;

/**
 * Récupère les catégories de permissions avec leurs permissions embarquées.
 * Accessible uniquement aux managers.
 */
export const useGetPermissionCategories = () => {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: getPermissionCategories,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Récupère les permissions distinctes de l'utilisateur connecté.
 * Cache 5 minutes via React Query staleTime, scoped par userId pour eviter
 * la fuite cross-user au switch de session (CW-22023).
 *
 * `placeholderData: undefined` override explicitement le default global
 * `keepPreviousData` (queryClient.ts). Sans cet override, quand la queryKey
 * passe de [..., userA] a [..., userB], React Query expose les permissions
 * de A en placeholder pendant le fetch de B. useAdminBootstrap ne checke
 * pas isPlaceholderData et copierait ces permissions stale dans useAdminStore
 * sous le userId de B (= meme fuite que celle qu'on cherche a corriger).
 */
export const useMyPermissions = () => {
  const { isAuthenticated, user } = useAuth();
  const userId = user?.id;

  return useQuery({
    queryKey: getMyPermissionsKey(userId),
    queryFn: getMyPermissions,
    staleTime: 5 * 60 * 1000,
    placeholderData: undefined,
    enabled: isAuthenticated && !!userId
  });
};
